import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import * as alertifyjs from 'alertifyjs';

// Interfaces según la documentación del endpoint /api/fees/versions/:code_concept
interface TimelineEntry {
  version_number: number;
  id: string;
  code: string;
  name: string;
  description: string;
  status: boolean;
  paused: boolean;
  action_type: 'CREATED' | 'EDITED' | 'DELETED';
  action_date: string; // ISO string format según documentación
  action_user: {
    _id: string;
    name: string;
    email: string;
  };
  observation: string;
  fee_config: {
    application_type: { _id: string; name: string; };
    frequency: { _id: string; name: string; };
    unit_of_measure: string;
    account_payment: { _id: string; name: string; };
    limit: string;
  };
  fees_count: number;
  is_current: boolean;
}

// Estructura completa según la documentación
interface VersionHistory {
  code_concept: string;
  total_versions: number;
  current_version: {
    _id: string;
    status: boolean;
    name: string;
  };
  timeline: TimelineEntry[];
  versions: any[]; // Datos completos para uso interno según documentación
}

@Component({
  selector: 'app-version-timeline',
  templateUrl: './version-timeline.component.html',
  styleUrls: ['./version-timeline.component.css']
})
export class VersionTimelineComponent implements OnInit, OnChanges {

  @Input() codeConcepto: string = '';
  @Input() isVisible: boolean = false;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() viewDetailsEvent = new EventEmitter<string>();

  timeline: VersionHistory | null = null;
  loading: boolean = false;
  error: string = '';

  constructor(private _user: UserService) { }

  ngOnInit(): void {
    if (this.codeConcepto && this.isVisible) {
      this.fetchVersionHistory();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['codeConcepto'] || changes['isVisible']) {
      if (this.codeConcepto && this.isVisible) {
        this.fetchVersionHistory();
      }
    }
  }

  fetchVersionHistory = async () => {
    if (!this.codeConcepto) return;
    
    try {
      this.loading = true;
      this.error = '';
      
      // Llamada al endpoint según documentación: GET /api/fees/versions/:code_concept
      const response = await this._user.getVersionTimeline(this.codeConcepto);
      
      if (response.success) {
        this.timeline = response.data;
      } else {
        throw new Error(response.error || 'Error desconocido');
      }
      
    } catch (error: any) {
      console.error('Error fetching version history:', error);
      this.error = error.message || 'Error al cargar el historial de versiones';
      alertifyjs.error('Error al cargar el historial de versiones');
    } finally {
      this.loading = false;
    }
  };

  getActionTypeText(actionType: string): string {
    const types: { [key: string]: string } = {
      'CREATED': 'Creado',
      'EDITED': 'Editado',
      'DELETED': 'Eliminado'
    };
    return types[actionType] || actionType;
  }

  getActionTypeIcon(actionType: string): string {
    const icons: { [key: string]: string } = {
      'CREATED': 'fas fa-plus-circle',
      'EDITED': 'fas fa-edit',
      'DELETED': 'fas fa-trash-alt'
    };
    return icons[actionType] || 'fas fa-circle';
  }

  getActionTypeColor(actionType: string): string {
    const colors: { [key: string]: string } = {
      'CREATED': 'success',
      'EDITED': 'warning',
      'DELETED': 'danger'
    };
    return colors[actionType] || 'secondary';
  }

  viewDetails = (versionId: string) => {
    this.viewDetailsEvent.emit(versionId);
  };

  close(): void {
    this.closeEvent.emit();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }

  getUserInitials(userName: string): string {
    if (!userName) return 'N/A';
    
    return userName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }
} 